import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { IoIosRemoveCircleOutline } from "react-icons/io";
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { fetchCoursesByTrack } from "../../../utils/courseUtils";
import { fetchBatchesByCourseSingle } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses, type Track } from "../../../utils/trackUtils";
import { compareMaybeNumeric } from "../../../utils/numericSortUtils";
import ConfirmationModal from "../../../components/ConfirmationModal";
import TestFilterSelect from "./TestFilterSelect";
import type {
  AssignBatch,
  AssignCourse,
  BatchConfigEntry,
  BatchContext,
  SelectedSubject,
  StudentData,
  StudentSearchTerms,
  TestData,
} from "./testTypes";
import {
  assignTestStudents,
  clearSubjectFinalAssessment,
  fetchAssignedStudents,
  fetchStudentsByCourseBatch,
  saveTestBatchAssignment,
} from "./testUtils";
import {
  saveConfigureDetailContext,
  type ConfigureDetailContext,
} from "./testRouteUtils";
import { useTestRoutes } from "./testRoutes";

type TestAssignStudentsPanelProps = {
  test: TestData;
  batchContext?: BatchContext;
  batchConfigs?: BatchConfigEntry[];
  subjects?: SelectedSubject[];
  slotId?: number;
};

const studentsPanelStyle: React.CSSProperties = {
  height: "calc(100vh - 260px)",
  overflow: "hidden",
};

const getStudentUsn = (student: StudentData) =>
  String(student.usn ?? student.USN ?? "").trim();

type AssignStudentSortKey = "student_id" | "usn" | "student_name" | "branch";

type AssignStudentSortConfig = {
  key: AssignStudentSortKey | "";
  direction: "asc" | "desc";
};

const EMPTY_SEARCH_TERMS: StudentSearchTerms = {
  id: "",
  usn: "",
  name: "",
  branch: "",
};

const EMPTY_SORT_CONFIG: AssignStudentSortConfig = {
  key: "",
  direction: "asc",
};

const ASSIGN_SORT_COLUMNS: {
  key: AssignStudentSortKey;
  label: string;
  className?: string;
}[] = [
  { key: "student_id", label: "Student ID", className: "text-center" },
  { key: "usn", label: "USN", className: "text-center" },
  { key: "student_name", label: "Name" },
  { key: "branch", label: "Branch", className: "text-center" },
];

const COLUMN_FILTERS: { key: keyof StudentSearchTerms; placeholder: string }[] =
  [
    { key: "id", placeholder: "Search ID" },
    { key: "usn", placeholder: "Search USN" },
    { key: "name", placeholder: "Search Name" },
    { key: "branch", placeholder: "Search Branch" },
  ];

const matchesStudentSearch = (
  student: StudentData,
  searchTerms: StudentSearchTerms
) => {
  const usn = getStudentUsn(student);
  return (
    (searchTerms.id === "" ||
      student.student_id
        .toLowerCase()
        .includes(searchTerms.id.toLowerCase())) &&
    (searchTerms.usn === "" ||
      usn.toLowerCase().includes(searchTerms.usn.toLowerCase())) &&
    (searchTerms.name === "" ||
      student.student_name
        .toLowerCase()
        .includes(searchTerms.name.toLowerCase())) &&
    (searchTerms.branch === "" ||
      student.branch.toLowerCase().includes(searchTerms.branch.toLowerCase()))
  );
};

const getAssignSortValue = (student: StudentData, key: AssignStudentSortKey) =>
  key === "usn" ? getStudentUsn(student) : String(student[key] ?? "");

const sortAssignStudents = (
  list: StudentData[],
  sortConfig: AssignStudentSortConfig,
  pinOtherSlotLast?: (student: StudentData) => boolean
) => {
  const sorted = [...list];
  sorted.sort((a, b) => {
    if (pinOtherSlotLast && !sortConfig.key) {
      const aOther = pinOtherSlotLast(a) ? 1 : 0;
      const bOther = pinOtherSlotLast(b) ? 1 : 0;
      if (aOther !== bOther) return aOther - bOther;
    }
    if (!sortConfig.key) return 0;
    return compareMaybeNumeric(
      getAssignSortValue(a, sortConfig.key),
      getAssignSortValue(b, sortConfig.key),
      sortConfig.direction
    );
  });
  return sorted;
};

const toggleAssignSort = (
  prev: AssignStudentSortConfig,
  key: AssignStudentSortKey
): AssignStudentSortConfig =>
  prev.key === key
    ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
    : { key, direction: "asc" };

const AssignSortHeader: React.FC<{
  label: string;
  sortKey: AssignStudentSortKey;
  sortConfig: AssignStudentSortConfig;
  onSort: (key: AssignStudentSortKey) => void;
  className?: string;
}> = ({ label, sortKey, sortConfig, onSort, className }) => (
  <th
    className={`text-nowrap user-select-none ${className ?? ""}`}
    style={{ cursor: "pointer" }}
    onClick={() => onSort(sortKey)}
    title={`Sort by ${label}`}
  >
    <div
      className={`d-flex align-items-center gap-1 ${
        className?.includes("text-center")
          ? "justify-content-center"
          : "justify-content-start"
      }`}
    >
      <span>{label}</span>
      {sortConfig.key === sortKey ? (
        sortConfig.direction === "asc" ? (
          <PiSortAscendingLight size={18} />
        ) : (
          <PiSortDescendingLight size={18} />
        )
      ) : (
        <HiOutlineDotsVertical size={16} className="text-muted" />
      )}
    </div>
  </th>
);

const AssignFilterRow: React.FC<{
  searchTerms: StudentSearchTerms;
  onChange: (next: StudentSearchTerms) => void;
  trailingCell?: React.ReactNode;
}> = ({ searchTerms, onChange, trailingCell }) => (
  <tr>
    {COLUMN_FILTERS.map((column) => (
      <th key={column.key} className="fw-normal">
        <Form.Control
          type="text"
          size="sm"
          autoComplete="off"
          placeholder={column.placeholder}
          value={searchTerms[column.key]}
          onChange={(e) =>
            onChange({ ...searchTerms, [column.key]: e.target.value })
          }
        />
      </th>
    ))}
    <th className="text-center">{trailingCell}</th>
  </tr>
);

const TestAssignStudentsPanel: React.FC<TestAssignStudentsPanelProps> = ({
  test: initialTest,
  batchContext,
  batchConfigs = [],
  subjects = [],
  slotId,
}) => {
  const navigate = useNavigate();
  const routes = useTestRoutes();
  const slimMode = !!batchContext;
  const slotMode = slotId != null && Number.isFinite(slotId);

  const isOnThisSlot = (student: StudentData) =>
    slotMode &&
    student.slot_id != null &&
    Number(student.slot_id) === Number(slotId);

  const isOnOtherSlot = (student: StudentData) =>
    slotMode &&
    student.slot_id != null &&
    Number(student.slot_id) !== Number(slotId);

  const [editingTest] = useState(initialTest);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [assignedStudents, setAssignedStudents] = useState<StudentData[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [assignTracks, setAssignTracks] = useState<Track[]>([]);
  const [assignTestCourses, setAssignTestCourses] = useState<AssignCourse[]>([]);
  const [assignTestBatches, setAssignTestBatches] = useState<AssignBatch[]>([]);
  const [loadingAssignTracks, setLoadingAssignTracks] = useState(false);
  const [loadingAssignTestCourses, setLoadingAssignTestCourses] = useState(false);
  const [loadingAssignTestBatches, setLoadingAssignTestBatches] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState("");
  const isInitializing = useRef(true);
  const [assignedStudentsLoading, setAssignedStudentsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingReassignStudent, setPendingReassignStudent] =
    useState<StudentData | null>(null);
  const [pendingSftClearStudent, setPendingSftClearStudent] =
    useState<StudentData | null>(null);
  const [isClearingSft, setIsClearingSft] = useState(false);
  const [assignSucceeded, setAssignSucceeded] = useState(false);
  const initialSlotAssignedIds = useRef<Set<string>>(new Set());
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedStudentType, setSelectedStudentType] = useState("");
  const [searchTerms, setSearchTerms] =
    useState<StudentSearchTerms>(EMPTY_SEARCH_TERMS);
  const [assignedSearchTerms, setAssignedSearchTerms] =
    useState<StudentSearchTerms>(EMPTY_SEARCH_TERMS);
  const [availableSort, setAvailableSort] =
    useState<AssignStudentSortConfig>(EMPTY_SORT_CONFIG);
  const [assignedSort, setAssignedSort] =
    useState<AssignStudentSortConfig>(EMPTY_SORT_CONFIG);

  const fetchStudents = async (
    course: string,
    batch: string,
    testId: string
  ) => {
    try {
      const response = await fetchStudentsByCourseBatch(course, batch, testId);
      const list: StudentData[] = response.students || [];
      setStudents(list);
      if (slotMode) {
        const onThisSlot = list.filter((student) => isOnThisSlot(student));
        setAssignedStudents(onThisSlot);
        initialSlotAssignedIds.current = new Set(
          onThisSlot.map((s) => s.student_id)
        );
      } else {
        const newlyAssignedStudents = list.filter(
          (student: StudentData) => student.is_assigned === true
        );
        setAssignedStudents((prev) => {
          const existingStudentIds = new Set(prev.map((s) => s.student_id));
          const newStudentsToAdd = newlyAssignedStudents.filter(
            (student: StudentData) => !existingStudentIds.has(student.student_id)
          );
          return [...prev, ...newStudentsToAdd];
        });
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchAlreadyAssignedStudents = async (testId: string) => {
    try {
      setAssignedStudentsLoading(true);
      const response = await fetchAssignedStudents(testId);
      const list: StudentData[] = response.students || [];
      if (slotMode) {
        setAssignedStudents(list.filter((s) => isOnThisSlot(s)));
      } else {
        setAssignedStudents(list);
      }
    } catch (error) {
      console.error("Error fetching already assigned students:", error);
      setShowUpdateModal(true);
      setModalMessage(
        "Failed to load already assigned students. Please try again."
      );
    } finally {
      setAssignedStudentsLoading(false);
    }
  };

  useEffect(() => {
    isInitializing.current = true;
    if (slimMode && batchContext) {
      fetchStudents(
        batchContext.course_id,
        batchContext.batch_id,
        initialTest.test_id
      );
    } else {
      setLoadingAssignTracks(true);
      fetchAllTracksForCourses()
        .then(setAssignTracks)
        .catch(console.error)
        .finally(() => setLoadingAssignTracks(false));
      if (initialTest.track_id) setSelectedTrack(initialTest.track_id);
      if (initialTest.course_id) setSelectedCourse(initialTest.course_id);
      if (initialTest.batch_id) setSelectedBatch(initialTest.batch_id);
      fetchAlreadyAssignedStudents(initialTest.test_id);
    }
    const initTimer = window.setTimeout(() => {
      isInitializing.current = false;
    }, 100);
    return () => window.clearTimeout(initTimer);
  }, [initialTest, slimMode, batchContext]);

  useEffect(() => {
    if (slimMode) return;
    setSearchTerms(EMPTY_SEARCH_TERMS);
    setAssignedSearchTerms(EMPTY_SEARCH_TERMS);
    setAvailableSort(EMPTY_SORT_CONFIG);
    setAssignedSort(EMPTY_SORT_CONFIG);
    setSelectedStudentType("");
  }, [selectedCourse, selectedBatch, slimMode]);

  useEffect(() => {
    if (slimMode || !selectedTrack) {
      if (!slimMode) setAssignTestCourses([]);
      return;
    }
    setLoadingAssignTestCourses(true);
    fetchCoursesByTrack(selectedTrack)
      .then(setAssignTestCourses)
      .catch(console.error)
      .finally(() => setLoadingAssignTestCourses(false));
    if (!isInitializing.current) {
      setSelectedCourse("");
      setSelectedBatch("");
      setAssignTestBatches([]);
      setStudents([]);
    }
  }, [selectedTrack, slimMode]);

  useEffect(() => {
    if (slimMode || !selectedCourse) {
      if (!slimMode) setAssignTestBatches([]);
      return;
    }
    setLoadingAssignTestBatches(true);
    fetchBatchesByCourseSingle(selectedCourse)
      .then(setAssignTestBatches)
      .catch(console.error)
      .finally(() => setLoadingAssignTestBatches(false));
    if (!isInitializing.current) {
      setSelectedBatch("");
      setStudents([]);
    }
  }, [selectedCourse, slimMode]);

  useEffect(() => {
    if (slimMode || !selectedCourse || !selectedBatch) return;
    fetchStudents(selectedCourse, selectedBatch, editingTest.test_id);
  }, [selectedCourse, selectedBatch, editingTest, slimMode]);

  const filteredStudents = useMemo(() => {
    const matched = students.filter((student) => {
      const matchesType =
        selectedStudentType === "" ||
        student.student_type === selectedStudentType;
      return matchesStudentSearch(student, searchTerms) && matchesType;
    });
    return sortAssignStudents(
      matched,
      availableSort,
      slotMode ? isOnOtherSlot : undefined
    );
  }, [
    students,
    searchTerms,
    selectedStudentType,
    availableSort,
    slotMode,
    slotId,
  ]);

  const selectableStudents = useMemo(
    () => filteredStudents.filter((s) => !isOnOtherSlot(s) && !s.sft_exists),
    [filteredStudents, slotId]
  );

  const filteredAssignedStudents = useMemo(
    () =>
      sortAssignStudents(
        assignedStudents.filter((student) =>
          matchesStudentSearch(student, assignedSearchTerms)
        ),
        assignedSort
      ),
    [assignedStudents, assignedSearchTerms, assignedSort]
  );

  const selectedCount = assignedStudents.length;
  const selectedLabel = `${selectedCount} student${
    selectedCount === 1 ? "" : "s"
  } selected`;

  const sftHoverTitle = (student: StudentData) => {
    const parts = [
      student.sft_existing_test_name,
      student.sft_existing_slot_label,
      student.sft_existing_date,
    ].filter(Boolean);
    return parts.length ? parts.join(" - ") : "Subject Final Test already exists";
  };

  useEffect(() => {
    if (selectableStudents.length > 0) {
      const allFilteredSelected = selectableStudents.every((fs) =>
        assignedStudents.some((as) => as.student_id === fs.student_id)
      );
      setSelectAll(allFilteredSelected);
    } else {
      setSelectAll(false);
    }
  }, [filteredStudents, assignedStudents, slotId]);

  const isStudentAssigned = (studentId: string) =>
    assignedStudents.some((s) => s.student_id === studentId);

  const handleStudentSelection = (
    student: StudentData,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    // Already selected here (including pending move) — toggle off
    if (assignedStudents.some((s) => s.student_id === student.student_id)) {
      setAssignedStudents((prev) => {
        const updated = prev.filter((s) => s.student_id !== student.student_id);
        const allFilteredSelected = selectableStudents.every((fs) =>
          updated.some((as) => as.student_id === fs.student_id)
        );
        setSelectAll(allFilteredSelected);
        return updated;
      });
      return;
    }
    if (isOnOtherSlot(student)) {
      setPendingReassignStudent(student);
      return;
    }
    if (student.sft_exists && student.sft_existing_test_id) {
      setPendingSftClearStudent(student);
      return;
    }
    setAssignedStudents((prev) => {
      const updated = [...prev, student];
      const allFilteredSelected = selectableStudents.every((fs) =>
        updated.some((as) => as.student_id === fs.student_id)
      );
      setSelectAll(allFilteredSelected);
      return updated;
    });
  };

  const confirmReassignStudent = () => {
    if (!pendingReassignStudent) return;
    const student = pendingReassignStudent;
    setPendingReassignStudent(null);
    setAssignedStudents((prev) => {
      if (prev.some((s) => s.student_id === student.student_id)) return prev;
      const updated = [...prev, { ...student, slot_id: slotId ?? student.slot_id }];
      const allFilteredSelected = selectableStudents.every((fs) =>
        updated.some((as) => as.student_id === fs.student_id)
      );
      setSelectAll(allFilteredSelected);
      return updated;
    });
  };

  const confirmClearSftStudent = async () => {
    const student = pendingSftClearStudent;
    const previousTestId = student?.sft_existing_test_id;
    if (!student || !previousTestId) return;
    setIsClearingSft(true);
    try {
      await clearSubjectFinalAssessment(
        editingTest.test_id,
        student.student_id,
        previousTestId
      );
      const cleared: StudentData = {
        ...student,
        sft_exists: false,
        sft_existing_assessment_id: null,
        sft_existing_test_id: null,
        sft_existing_test_name: null,
        sft_existing_subject_name: null,
        sft_existing_slot_label: null,
        sft_existing_date: null,
        slot_id: slotId ?? student.slot_id,
      };
      setStudents((prev) =>
        prev.map((s) => (s.student_id === student.student_id ? cleared : s))
      );
      setPendingSftClearStudent(null);
      setAssignedStudents((prev) => {
        if (prev.some((s) => s.student_id === cleared.student_id)) return prev;
        return [...prev, cleared];
      });
    } catch (error: unknown) {
      console.error("Error clearing previous SFT assessment:", error);
      setPendingSftClearStudent(null);
      setAssignSucceeded(false);
      setShowUpdateModal(true);
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail ||
        (error as Error)?.message ||
        "Failed to clear previous Subject Final Test";
      setModalMessage(
        typeof detail === "string"
          ? detail
          : "Failed to clear previous Subject Final Test"
      );
    } finally {
      setIsClearingSft(false);
    }
  };

  const handleRowClick = (student: StudentData) => {
    handleStudentSelection(student, {
      stopPropagation: () => {},
    } as React.MouseEvent);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setAssignedStudents((prev) => {
        const existingIds = new Set(prev.map((s) => s.student_id));
        const newStudents = selectableStudents.filter(
          (s) => !existingIds.has(s.student_id)
        );
        return [...prev, ...newStudents];
      });
    } else {
      setAssignedStudents((prev) => {
        const filteredIds = new Set(
          selectableStudents.map((s) => s.student_id)
        );
        return prev.filter((s) => !filteredIds.has(s.student_id));
      });
    }
  };

  const navigateToConfigure = (nextBatchConfigs: BatchConfigEntry[]) => {
    const context: ConfigureDetailContext = {
      test_id: editingTest.test_id,
      subjects,
    };
    saveConfigureDetailContext(context);
    navigate(routes.configureDetail(editingTest.title, editingTest.test_id), {
      state: {
        test: editingTest,
        subjects,
        batchConfigs: nextBatchConfigs,
      },
    });
  };

  /** Post-save only: merge current assignedStudents into batchConfigs before navigating back. */
  const navigateBackToConfigure = (updatedBatchConfigs?: BatchConfigEntry[]) => {
    const studentIds = assignedStudents.map((s) => s.student_id);
    const slotKey =
      slotMode && slotId != null ? String(slotId) : null;
    const nextBatchConfigs = (updatedBatchConfigs ?? batchConfigs).map((b) => {
      if (!(slimMode && batchContext && b.batch_id === batchContext.batch_id)) {
        return b;
      }
      // Slot mode: replace this slot's previous IDs only; keep other slots' IDs
      let mergedIds = studentIds;
      if (slotMode) {
        const prev = b.assignedStudentIds || [];
        const withoutThisSlot = prev.filter(
          (id) => !initialSlotAssignedIds.current.has(id)
        );
        mergedIds = Array.from(new Set([...withoutThisSlot, ...studentIds]));
      }
      const nextSlotCounts = { ...(b.slot_assigned_counts || {}) };
      if (slotKey) {
        nextSlotCounts[slotKey] = studentIds.length;
      }
      const hasAnyAssigned =
        mergedIds.length > 0 ||
        Object.values(nextSlotCounts).some((n) => Number(n) > 0);
      let nextStatus = b.status;
      if (!hasAnyAssigned && (b.status === "draft" || b.status === "partial")) {
        nextStatus = "draft";
      } else if (hasAnyAssigned && b.status === "draft") {
        nextStatus = "partial";
      }
      return {
        ...b,
        assignedStudentIds: mergedIds,
        slot_assigned_counts: nextSlotCounts,
        status: nextStatus,
      };
    });
    navigateToConfigure(nextBatchConfigs);
  };

  const handleConfirmAssignment = async () => {
    try {
      setIsAssigning(true);
      const matchingBatch = batchConfigs.find(
        (row) =>
          slimMode &&
          batchContext &&
          row.batch_id === batchContext.batch_id
      );
      if (slimMode && batchContext) {
        await saveTestBatchAssignment(editingTest, {
          track_id: batchContext.track_id || matchingBatch?.track_id || "",
          course_id: batchContext.course_id || matchingBatch?.course_id || "",
          batch_id: batchContext.batch_id,
          subjects: matchingBatch?.subjects ?? subjects.map((s) => s.subject_name),
        });
      } else if (selectedTrack && selectedCourse && selectedBatch) {
        await saveTestBatchAssignment(editingTest, {
          track_id: selectedTrack,
          course_id: selectedCourse,
          batch_id: selectedBatch,
          subjects: subjects.map((s) => s.subject_name),
        });
      }
      await assignTestStudents(
        editingTest.test_id,
        assignedStudents.map((s) => s.student_id),
        slimMode
          ? batchContext?.batch_id
          : selectedBatch || undefined,
        slotMode ? Number(slotId) : undefined
      );
      setShowConfirmModal(false);
      if (slimMode) {
        navigateBackToConfigure();
      } else {
        setAssignSucceeded(true);
        setShowUpdateModal(true);
        setModalMessage("Students successfully assigned to test!");
      }
    } catch (error: unknown) {
      console.error("Error assigning students:", error);
      setShowConfirmModal(false);
      setAssignSucceeded(false);
      setShowUpdateModal(true);
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail ||
        (error as Error)?.message ||
        "Failed to assign students to test";
      setModalMessage(
        typeof detail === "string" ? detail : "Failed to assign students to test"
      );
    } finally {
      setIsAssigning(false);
    }
  };

  const openSaveConfirm = () => {
    if (
      slotMode &&
      assignedStudents.length === 0 &&
      initialSlotAssignedIds.current.size === 0
    ) {
      return;
    }
    setShowConfirmModal(true);
  };

  const isClearingSlot =
    slotMode &&
    assignedStudents.length === 0 &&
    initialSlotAssignedIds.current.size > 0;

  return (
    <div>
      <div className="row mx-0">
        <div className="col-12">
          <div className="border border-light p-2">
            {slimMode && batchContext ? (
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex gap-2">
                <Badge bg="light" text="dark" className="border">
                  Track: {batchContext.track_name || batchContext.track_id}
                </Badge>
                <Badge bg="light" text="dark" className="border">
                  Course: {batchContext.course_name || batchContext.course_id}
                </Badge>
                <Badge bg="light" text="dark" className="border">
                  Batch: {batchContext.batch_name || batchContext.batch_id}
                </Badge>
                {slotMode && (
                  <Badge bg="primary">
                    Assigning to slot (id {slotId})
                  </Badge>
                )}
                </div>
                <div>
                <span className="small text-muted">
                {students.length > 0 ? `${selectedLabel}` : ""}
              </span>
              </div>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <TestFilterSelect
                  label="Track"
                  value={selectedTrack}
                  onChange={setSelectedTrack}
                  disabled={loadingAssignTracks}
                  placeholder={
                    loadingAssignTracks ? "Loading tracks..." : "Select Track"
                  }
                  options={assignTracks.map((track) => ({
                    value: track.track_id,
                    label: track.track_name,
                  }))}
                />
                <TestFilterSelect
                  label="Course"
                  value={selectedCourse}
                  onChange={setSelectedCourse}
                  disabled={loadingAssignTestCourses || !selectedTrack}
                  placeholder="Select Course"
                  options={assignTestCourses.map((course) => ({
                    value: course.course_id,
                    label: course.course_name,
                  }))}
                />
                <TestFilterSelect
                  label="Batch"
                  value={selectedBatch}
                  onChange={setSelectedBatch}
                  disabled={loadingAssignTestBatches || !selectedCourse}
                  placeholder="Select Batch"
                  options={assignTestBatches.map((batch) => ({
                    value: batch.batch_id,
                    label: batch.batch_name,
                  }))}
                />
                <TestFilterSelect
                  label="Student Type"
                  value={selectedStudentType}
                  onChange={setSelectedStudentType}
                  placeholder="Student Type"
                  options={[
                    { value: "SW", label: "SW" },
                    { value: "EU", label: "EU" },
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row mx-0 mt-1">
        <div className="col-6">
          <div
            className="border border-secondary rounded p-2 d-flex flex-column assign-students-grid"
            style={studentsPanelStyle}
          >
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    {ASSIGN_SORT_COLUMNS.map((column) => (
                      <AssignSortHeader
                        key={column.key}
                        label={column.label}
                        sortKey={column.key}
                        sortConfig={availableSort}
                        onSort={(key) =>
                          setAvailableSort((prev) => toggleAssignSort(prev, key))
                        }
                        className={column.className}
                      />
                    ))}
                    <th className="text-center">Select</th>
                  </tr>
                  <AssignFilterRow
                    searchTerms={searchTerms}
                    onChange={setSearchTerms}
                    trailingCell={
                      <Form.Check
                        type="checkbox"
                        checked={selectAll}
                        disabled={selectableStudents.length === 0}
                        onChange={handleSelectAll}
                        title="Select all visible students"
                      />
                    }
                  />
                </thead>
                <tbody>
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => {
                      const selectedHere = isStudentAssigned(student.student_id);
                      const otherSlot = isOnOtherSlot(student) && !selectedHere;
                      const sftConflict = !!student.sft_exists && !selectedHere;
                      return (
                        <tr
                          key={student.student_id}
                          className={
                            otherSlot || sftConflict
                              ? "table-secondary"
                              : selectedHere
                                ? "table-active"
                                : ""
                          }
                          style={{ cursor: "pointer" }}
                          title={
                            otherSlot
                              ? "Click to move this student to the current slot"
                              : sftConflict
                                ? sftHoverTitle(student)
                                : undefined
                          }
                          onClick={() => handleRowClick(student)}
                        >
                          <td className="text-center">{student.student_id}</td>
                          <td className="text-center">
                            {getStudentUsn(student)}
                          </td>
                          <td>
                            <span className="me-2">{student.student_name}</span>
                            {otherSlot && (
                              <Badge bg="secondary">
                                {student.slot_label || `Slot ${student.slot_id}`}
                              </Badge>
                            )}
                            {sftConflict && (
                              <Badge
                                bg="warning"
                                text="dark"
                                title={sftHoverTitle(student)}
                              >
                                SFT exists
                              </Badge>
                            )}
                          </td>
                          <td className="text-center">{student.branch}</td>
                          <td
                            className="text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={selectedHere}
                              onChange={(e) =>
                                handleStudentSelection(
                                  student,
                                  e.nativeEvent as unknown as React.MouseEvent
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        {students.length === 0
                          ? "No students available"
                          : "No students match the current filters"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div
            className="border border-secondary rounded p-2 d-flex flex-column assign-students-grid"
            style={studentsPanelStyle}
          >
            <div
              className={`flex-grow-1 overflow-auto${
                assignedStudentsLoading
                  ? " d-flex justify-content-center align-items-center"
                  : ""
              }`}
              style={{ minHeight: 0 }}
            >
              {assignedStudentsLoading ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">
                    Loading assigned students...
                  </span>
                </Spinner>
              ) : (
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      {ASSIGN_SORT_COLUMNS.map((column) => (
                        <AssignSortHeader
                          key={column.key}
                          label={column.label}
                          sortKey={column.key}
                          sortConfig={assignedSort}
                          onSort={(key) =>
                            setAssignedSort((prev) =>
                              toggleAssignSort(prev, key)
                            )
                          }
                          className={column.className}
                        />
                      ))}
                      <th className="text-center">Remove</th>
                    </tr>
                    <AssignFilterRow
                      searchTerms={assignedSearchTerms}
                      onChange={setAssignedSearchTerms}
                    />
                  </thead>
                  <tbody>
                    {assignedStudents.length > 0 ? (
                      filteredAssignedStudents.length > 0 ? (
                        filteredAssignedStudents.map((student) => (
                          <tr key={student.student_id}>
                            <td className="text-center">
                              {student.student_id}
                            </td>
                            <td className="text-center">
                              {getStudentUsn(student)}
                            </td>
                            <td>{student.student_name}</td>
                            <td className="text-center">{student.branch}</td>
                            <td
                              className="text-center"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleRowClick(student)}
                            >
                              <IoIosRemoveCircleOutline size={22} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No assigned students match the current filters
                          </td>
                        </tr>
                      )
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No students assigned yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
            <div className="mt-3 text-end">
              <Button
                variant="primary"
                onClick={openSaveConfirm}
                disabled={
                  (!slotMode && assignedStudents.length === 0) ||
                  (slotMode &&
                    assignedStudents.length === 0 &&
                    initialSlotAssignedIds.current.size === 0)
                }
              >
                {slimMode ? "Save" : "Assign Test"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showUpdateModal}
        onHide={() => {
          setShowUpdateModal(false);
          if (assignSucceeded) {
            navigate(routes.configure);
          }
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
      </Modal>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAssignment}
        title={
          isClearingSlot
            ? "Clear slot assignments"
            : slimMode
              ? "Save Student Assignment"
              : "Confirm Test Assignment"
        }
        message={
          isClearingSlot
            ? "No students are selected. Saving will remove all students currently assigned to this slot. Continue?"
            : slimMode
              ? "Save selected students for this batch?"
              : "Are you sure you want to assign test?"
        }
        confirmText={isClearingSlot ? "Clear slot" : "Yes"}
        cancelText="Cancel"
        variant={isClearingSlot ? "warning" : "primary"}
        isLoading={isAssigning}
      />

      <ConfirmationModal
        show={pendingReassignStudent != null}
        onHide={() => setPendingReassignStudent(null)}
        onConfirm={confirmReassignStudent}
        title="Move student to this slot"
        message={
          pendingReassignStudent
            ? `${pendingReassignStudent.student_name} (${pendingReassignStudent.student_id}) is on ${
                pendingReassignStudent.slot_label ||
                `Slot ${pendingReassignStudent.slot_id}`
              }. Move them to this slot? Their previous attempt on the other slot will be removed (soft-deleted) and they get a fresh Pending assignment here.`
            : ""
        }
        confirmText="Move"
        cancelText="Cancel"
        variant="warning"
      />

      <ConfirmationModal
        show={pendingSftClearStudent != null}
        onHide={() => !isClearingSft && setPendingSftClearStudent(null)}
        onConfirm={confirmClearSftStudent}
        title="Clear previous Subject Final Test"
        message={
          pendingSftClearStudent
            ? `Do you want to clear the student's previous test "${
                pendingSftClearStudent.sft_existing_test_name ||
                pendingSftClearStudent.sft_existing_test_id
              }" data of ${
                pendingSftClearStudent.sft_existing_subject_name || "Subject"
              } Subject Final Test? This clears all active Subject Final Tests for that subject so you can assign this one.`
            : ""
        }
        confirmText="Clear & select"
        cancelText="Cancel"
        variant="warning"
        isLoading={isClearingSft}
      />
    </div>
  );
};

export default TestAssignStudentsPanel;
